import React, { useState } from 'react';
import { utilitiesData } from '../../data/knowledgeBank/utilitiesData';
import { FiGrid, FiExternalLink } from 'react-icons/fi';

export default function Utilities() {
  const [activeTab, setActiveTab] = useState(utilitiesData[0]?.id || '');

  const activeUtility = utilitiesData.find(u => u.id === activeTab) || utilitiesData[0];

  // Helper to render block-based content
  const renderBlock = (block, idx) => {
    switch (block.type) {
      case 'heading':
        const HeadingTag = block.level && ['h2', 'h3', 'h4', 'h5'].includes(block.level) ? block.level : 'h3';
        return (
          <HeadingTag 
            key={idx}
            style={{ 
              color: 'var(--primary-dark)',
              marginTop: '28px',
              marginBottom: '14px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              borderBottom: HeadingTag === 'h2' ? '1px solid var(--gray-200)' : 'none',
              paddingBottom: HeadingTag === 'h2' ? '8px' : '0'
            }}
          >
            {block.text}
          </HeadingTag>
        );
      case 'paragraph':
        return (
          <p 
            key={idx} 
            style={{ 
              color: 'var(--text-dark)', 
              fontSize: '0.98rem', 
              lineHeight: '1.65', 
              marginBottom: '16px' 
            }}
          >
            {block.text}
          </p>
        );
      case 'list':
        return (
          <ul 
            key={idx} 
            style={{ 
              listStyleType: block.style === 'numbered' ? 'decimal' : 'disc', 
              paddingLeft: '24px', 
              marginBottom: '20px',
              color: 'var(--text-dark)'
            }}
          >
            {block.items.map((item, i) => (
              <li key={i} style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                {item}
              </li>
            ))}
          </ul>
        );
      case 'table':
        if (!block.rows || block.rows.length === 0) return null;
        
        // Assume first row is header
        const headers = block.rows[0];
        const rows = block.rows.slice(1);
        
        return (
          <div key={idx} style={{ overflowX: 'auto', margin: '24px 0', boxShadow: 'var(--shadow-sm)', borderRadius: 'var(--radius-sm)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left', minWidth: '600px' }}>
              <thead>
                <tr style={{ background: 'var(--off-white)', borderBottom: '2px solid var(--gray-200)' }}>
                  {headers.map((h, i) => (
                    <th key={i} style={{ padding: '12px', color: 'var(--text-dark)', fontWeight: 700, border: '1px solid var(--gray-200)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={rIdx} style={{ borderBottom: '1px solid var(--gray-100)', background: rIdx % 2 === 0 ? 'var(--white)' : '#fbfcfd' }}>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} style={{ padding: '12px', color: 'var(--text-dark)', border: '1px solid var(--gray-200)' }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-hero">
        <h1>Reference <span>Utilities</span></h1>
        <p>Tax rates, depreciation schedules, filing checklists, and financial tables</p>
      </div>

      <main style={{ padding: '60px 0', background: 'var(--white)' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
            {/* Sidebar Navigator */}
            <aside style={{ flex: '1 1 280px', maxWidth: '340px' }}>
              <div 
                style={{ 
                  background: 'var(--off-white)', 
                  border: '1px solid var(--gray-200)', 
                  borderRadius: 'var(--radius-md)', 
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  position: 'sticky',
                  top: '110px'
                }}
              >
                <h3 
                  style={{ 
                    fontSize: '1.05rem', 
                    color: 'var(--text-dark)', 
                    marginBottom: '12px', 
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  Quick Reference List
                </h3>
                {utilitiesData.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setActiveTab(u.id)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: '0.9rem',
                      fontWeight: activeTab === u.id ? 700 : 500,
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      background: activeTab === u.id ? 'var(--primary)' : 'transparent',
                      color: activeTab === u.id ? 'var(--white)' : 'var(--text-dark)',
                      cursor: 'pointer',
                      transition: 'all var(--transition)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FiGrid size={14} /> {u.name}
                  </button>
                ))}
              </div>
            </aside>

            {/* Content Display */}
            <article style={{ flex: '3 1 500px' }}>
              {activeUtility ? (
                <div style={{ background: 'var(--white)', padding: '10px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '2px solid var(--primary)', paddingBottom: '16px' }}>
                    <h2 
                      style={{ 
                        fontSize: '2rem', 
                        color: 'var(--primary-dark)',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700 
                      }}
                    >
                      {activeUtility.name}
                    </h2>
                    <a
                      href={activeUtility.originalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-outline"
                      style={{
                        padding: '8px 16px',
                        fontSize: '0.82rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        borderRadius: 'var(--radius-sm)'
                      }}
                    >
                      Source <FiExternalLink size={12} />
                    </a>
                  </div>

                  <div>
                    {activeUtility.blocks.map((block, idx) => renderBlock(block, idx))}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                  Select a utility schedule from the list to display details.
                </div>
              )}
            </article>
          </div>
        </div>
      </main>
    </div>
  );
}
